import React, {useEffect, useRef, useState} from 'react';
import {MailTemplateDto} from "../../../../../modules/mail/dto/MailTemplateDto";
import {FormattedMessage, useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {MailTemplateApi} from "../../../../../modules/mail/api/MailTemplateApi";

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import RichTextEditor from "../../../../../components/RichTextEditor";
import {MessageUtils} from "../../../../../utils/MessageUtils";

export type DetailDialogProps = {
    open: boolean;
    mailTemplate: MailTemplateDto;
    onClose: () => void;
    onOk: () => void;
};

const DetailDialog: React.FC<DetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.mailTemplate.id ? 'MAIL_TEMPLATE_UPDATE' : 'MAIL_TEMPLATE_CREATE';

    const [innerMailTemplate, setInnerMailTemplate] = useState<MailTemplateDto>(props.mailTemplate);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    const editorRef = useRef(null);

    useEffect(() => {
        if (props.open) {
            setInnerMailTemplate({...props.mailTemplate});
        }
    }, [props.open]);

    const handleChange = (name: string, value: any) => {
        setInnerMailTemplate(previousInnerMailTemplate => ({...previousInnerMailTemplate, [name]: value}));
    };

    const handleOk = async () => {
        if (validate()) {
            if (editorRef.current) {
                // @ts-ignore
                innerMailTemplate.mailContent = editorRef.current.getContent();
            }
            if (props.mailTemplate.id) {
                const responseResult = await MailTemplateApi.updateMailTemplate(innerMailTemplate);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'MAIL_TEMPLATE_UPDATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await MailTemplateApi.createMailTemplate(innerMailTemplate);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'MAIL_TEMPLATE_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const handleCancel = () => {
        setMessageMap(new Map<string, string>());
        props.onClose();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();

        if (innerMailTemplate.code === '') {
            newMessageMap.set('code', intl.formatMessage({id: 'MAIL_TEMPLATE_CODE_REQUIRED'}));
        }
        if (innerMailTemplate.name === undefined || innerMailTemplate.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'MAIL_TEMPLATE_NAME_REQUIRED'}));
        }
        if (innerMailTemplate.mailFrom === '') {
            newMessageMap.set('mailFrom', intl.formatMessage({id: 'MAIL_TEMPLATE_MAIL_FROM_REQUIRED'}));
        }
        if (innerMailTemplate.mailTo === '') {
            newMessageMap.set('mailTo', intl.formatMessage({id: 'MAIL_TEMPLATE_MAIL_TO_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true} maxWidth={'md'}>
            <DialogTitle>{intl.formatMessage({id: title})}</DialogTitle>
            <DialogContent>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_CODE"})}
                               required={true} errorText={messageMap.get("code")}
                               value={innerMailTemplate.code}
                               onChange={(value) => {
                                   handleChange("code", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_NAME"})}
                               required={true} errorText={messageMap.get("name")}
                               value={innerMailTemplate.name}
                               onChange={(value) => {
                                   handleChange("name", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_MAIL_FROM"})}
                               required={true} errorText={messageMap.get("mailFrom")}
                               value={innerMailTemplate.mailFrom}
                               onChange={(value) => {
                                   handleChange("mailFrom", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_MAIL_TO"})}
                               required={true} errorText={messageMap.get("mailTo")}
                               value={innerMailTemplate.mailTo}
                               onChange={(value) => {
                                   handleChange("mailTo", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_MAIL_CC"})}
                               value={innerMailTemplate.mailCc}
                               onChange={(value) => {
                                   handleChange("mailCc", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_MAIL_BCC"})}
                               value={innerMailTemplate.mailBcc}
                               onChange={(value) => {
                                   handleChange("mailBcc", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "MAIL_TEMPLATE_MAIL_TITLE"})}
                               value={innerMailTemplate.mailTitle}
                               onChange={(value) => {
                                   handleChange("mailTitle", value)
                               }}/>
                <RichTextEditor editorRef={editorRef} value={innerMailTemplate.mailContent}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleCancel}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default DetailDialog;
